<script>
  import { onMount, onDestroy } from 'svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '../stores/selectionStore';
  import { userRoles, userReadOnly } from '../stores/sessionStore';
  import { hasAccessUiAction } from '$utils/permissionUtils.js';
  import { studyProperties } from '../stores/configStore';
  import { isCompactMode } from '../stores/uiStore';
  import StudyFiles from './StudyFiles.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
  import { 
    faPaperclip, 
    faPencil,
    faCog,
    faHistory
  } from '@fortawesome/pro-light-svg-icons';
  import { fade, slide } from 'svelte/transition';
  import { createEventDispatcher } from 'svelte';
  const dispatch = createEventDispatcher();
  const demoMode = import.meta.env.VITE_DEMO_MODE === 'true';

  // Prop to control whether the options menu is shown
  export let showOptionsMenu = false;
  export let canViewAuditLogs = false;

  function formatDate(dateString) {
    if (!dateString) return 'n/a';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  onMount(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  onDestroy(() => {});

  $: canEditStudyFromHeader = hasAccessUiAction(
    'studies.edit',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canConfigureStudyFromHeader = hasAccessUiAction(
    'studies.configure',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  let showFiles = false;
  let showMenu = false;
  let isStudyFilesEnabled = false;

  $: if ($studyProperties?.features?.documentManagement?.study !== undefined) {
    isStudyFilesEnabled = $studyProperties.features.documentManagement.study;
  }

  $: if (isStudyFilesEnabled && $studyProperties?.features?.documentManagement?.autoShowStudy) {
    showFiles = true;
  }

  function handleMenuClick(event) {
    event.stopPropagation();
    showMenu = !showMenu;
  }

  function handleMenuClose() {
    showMenu = false;
  }

  // Add click handler to close menu when clicking outside
  function handleClickOutside(event) {
    if (showMenu && !event.target.closest('.menu-container')) {
      handleMenuClose();
    }
  }
</script>

{#if $selectedStudy}
  <div class="mx-1 mt-3 mb-0 p-4 rounded-lg shadow-md overflow-y-auto relative">
    <div
      class="absolute inset-0 bg-[url('/title_bg_light.jpg')] dark:bg-[url('/title_bg_dark.jpg')] bg-cover bg-center bg-no-repeat opacity-50 dark:opacity-75"
    ></div>

    <!-- Vertical Study Label -->
    <div class="absolute left-0 top-0 bottom-0 flex items-center justify-center w-8 z-20 bg-gray-100 dark:bg-gray-800">
      <div class="writing-mode-vertical text-xs font-black text-gray-600 dark:text-gray-300 tracking-wider">
        STUDY
      </div>
    </div>

    <div class="relative z-10 ml-8">
      {#if showOptionsMenu}
        <div class="menu-container absolute right-1 top-0 flex-none">
          <button
            on:click={handleMenuClick}
            class="w-8 h-8 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:text-sky-800 dark:hover:text-white/70"
            title="More options"
          >
            <FontAwesomeIcon icon={faEllipsis} />
          </button>
          {#if showMenu}
            <div class="fixed transform -translate-x-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-[9999]">
              {#if isStudyFilesEnabled}
                <button
                  on:click={() => {
                    showFiles = !showFiles;
                    handleMenuClose();
                  }}
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                >
                  <span>{showFiles ? 'Hide documents' : 'Show documents'}</span>
                  <FontAwesomeIcon icon={faPaperclip} class="text-sky-800 dark:text-white/70" />
                </button>
              {/if}
              <button
                on:click={() => {
                  console.log('Edit button clicked');
                  dispatch('edit', $selectedStudy);
                  handleMenuClose();
                }}
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                disabled={!canEditStudyFromHeader}
                class:opacity-50={!canEditStudyFromHeader}
              >
                <span>Edit study</span>
                <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
              </button>
              <button
                on:click={() => {
                  console.log('Configure button clicked');
                  dispatch('configure', $selectedStudy);
                  handleMenuClose();
                }}
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                disabled={!canConfigureStudyFromHeader}
                class:opacity-50={!canConfigureStudyFromHeader}
              >
                <span>Configure study</span>
                <FontAwesomeIcon icon={faCog} class="text-sky-800 dark:text-white/70" />
              </button>
              <button
                on:click={() => {
                  console.log('Audit log button clicked');
                  dispatch('auditLog', $selectedStudy);
                  handleMenuClose();
                }}
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                disabled={!canViewAuditLogs}
                class:opacity-50={!canViewAuditLogs}
              >
                <span>View audit logs</span>
                <FontAwesomeIcon icon={faHistory} class="text-sky-800 dark:text-white/70" />
              </button>
            </div>
          {/if}
        </div>
      {/if}
      <div class="flex gap-3 mt-0 items-start justify-between">
        <div class="flex gap-3">
          <div class="w-72">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Name</label>
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">
              {$selectedStudy.studyName}
            </div>
          </div>
          <div class="w-72">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Protocol</label>
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">{$selectedStudy.protocolNumber}</div>
          </div>
          <div class="w-72">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Status</label>
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">{$selectedStudy.statusLabel}</div>
          </div>
        </div>
      </div>
      {#if !$isCompactMode}
        <div class="flex gap-3 mt-2">
          <div class="w-72">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Sponsor</label>
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate" class:opacity-0={!$selectedStudy.sponsor}>
              {$selectedStudy.sponsor ? $selectedStudy.sponsor : 'n/a'}
            </div>
          </div>
          <div class="w-72">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Start Date</label>
            <!-- SPTL-535: blank (opacity-0) when the date is missing — exact
                 Pre-Security Revamp parity. SPTL-494 briefly rendered 'n/a'
                 here; QA flagged the divergence from the Pre-Security Revamp
                 portal, which hides the value. -->
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate" class:opacity-0={!$selectedStudy.startDate}>
              {formatDate($selectedStudy.startDate)}
            </div>
          </div>
          <div class="w-72">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">End Date</label>
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate" class:opacity-0={!$selectedStudy.stopDate}>
              {formatDate($selectedStudy.stopDate)}
            </div>
          </div>
        </div>
      {/if}
    </div>
  </div> 

  {#if showFiles}
    <div class="mx-1 mt-3" in:fade={{ duration: 200 }} out:fade={{ duration: 200 }}>
      <div in:slide={{ duration: 200 }} out:slide={{ duration: 200 }}>
        <StudyFiles on:close={() => showFiles = false} />
      </div>
    </div>
  {/if}
{/if}

<style>
  .writing-mode-vertical {
    writing-mode: vertical-rl;
    text-orientation: mixed;
    transform: rotate(180deg);
  }
</style>
